/**
 * Clinical range severity for vitals readings — green / yellow / red.
 *
 * Bands match ClientHealthConfig.customThresholds + WatchThresholdSettings /
 * cortex-be vitalsThresholdEngine (`<metric>_warn_*` / `<metric>_crit_*`).
 * Keep DEFAULT_VITAL_BANDS in lockstep with WatchThresholdSettings.VITAL_THRESHOLDS
 * (unit test asserts parity).
 */
import { severity, severityToken } from "../styles/designTokens";

/** Short web keys → vitalsThresholdEngine metric types. */
export const METRIC_ALIASES = Object.freeze({
  hr: "heart_rate",
  spo2: "spo2",
  blood_oxygen: "spo2",
  oxygen_saturation: "spo2",
  rr: "respiratory_rate",
  breathing_rate: "respiratory_rate",
  breathingRate: "respiratory_rate",
  heartRate: "heart_rate",
});

/**
 * Baseline defaults — mirror WatchThresholdSettings.VITAL_THRESHOLDS defs.
 * Keys: `<metric>_<warn|crit>_<below|above>`.
 */
export const DEFAULT_VITAL_BANDS = Object.freeze({
  spo2_warn_below: 92,
  spo2_crit_below: 88,
  heart_rate_warn_below: 50,
  heart_rate_warn_above: 110,
  heart_rate_crit_below: 40,
  heart_rate_crit_above: 130,
  resting_heart_rate_warn_below: 45,
  resting_heart_rate_warn_above: 100,
  resting_heart_rate_crit_below: 38,
  resting_heart_rate_crit_above: 120,
  walking_heart_rate_warn_above: 140,
  walking_heart_rate_crit_above: 160,
  respiratory_rate_warn_below: 10,
  respiratory_rate_warn_above: 24,
  respiratory_rate_crit_below: 8,
  respiratory_rate_crit_above: 28,
  blood_pressure_systolic_warn_above: 140,
  blood_pressure_systolic_crit_above: 180,
  blood_pressure_diastolic_warn_above: 90,
  blood_pressure_diastolic_crit_above: 120,
});

export function resolveMetricType(metricType) {
  if (!metricType || typeof metricType !== "string") return null;
  return METRIC_ALIASES[metricType] || metricType;
}

/**
 * Effective band map: defaults overwritten by finite custom overrides.
 * @param {object} [customThresholds]
 * @returns {Record<string, number>}
 */
export function effectiveThresholds(customThresholds = {}) {
  const out = { ...DEFAULT_VITAL_BANDS };
  if (!customThresholds || typeof customThresholds !== "object") return out;
  for (const [k, v] of Object.entries(customThresholds)) {
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  }
  return out;
}

function band(thresholds, metric, bound) {
  const v = thresholds[`${metric}_${bound}`];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Classify a numeric reading against warn/crit bands.
 * Critical wins over warning. Missing bands are skipped (one-sided metrics OK).
 *
 * 'unclassified' is an honest "we have no bands (or no reading) for this
 * metric" — a metric we cannot judge must never render clinically green
 * (Watch extras like body_temperature / walking_speed / HRV, and the map's
 * temp chip, have no bands here).
 *
 * @returns {'normal'|'caution'|'critical'|'unclassified'}
 */
export function classifyVitalSeverity(value, metricType, customThresholds = {}) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "unclassified";
  // Every banded metric here is a vital sign where 0 is physiologically ABSENT
  // rather than a measurement (HR, resting/walking HR, SpO2, RR, both BP
  // halves). Classifying it would paint missing data as a clinical emergency —
  // 0 <= heart_rate_crit_below → "critical" — the red mirror of the
  // green-on-missing bug. Callers pass readings straight through, so the
  // react-app#374 zero-absence contract is enforced here, once.
  if (value <= 0) return "unclassified";
  const metric = resolveMetricType(metricType);
  if (!metric) return "unclassified";

  const t = effectiveThresholds(customThresholds);
  const critBelow = band(t, metric, "crit_below");
  const critAbove = band(t, metric, "crit_above");
  const warnBelow = band(t, metric, "warn_below");
  const warnAbove = band(t, metric, "warn_above");

  if (
    critBelow === null &&
    critAbove === null &&
    warnBelow === null &&
    warnAbove === null
  ) {
    return "unclassified";
  }

  if (critBelow !== null && value <= critBelow) return "critical";
  if (critAbove !== null && value >= critAbove) return "critical";
  if (warnBelow !== null && value <= warnBelow) return "caution";
  if (warnAbove !== null && value >= warnAbove) return "caution";
  return "normal";
}

/**
 * Worst of several levels (critical > caution > normal > unclassified).
 * @param {...('normal'|'caution'|'critical'|'unclassified')} levels
 */
export function worstVitalSeverity(...levels) {
  const rank = { unclassified: 0, normal: 1, caution: 2, critical: 3 };
  let worst = "unclassified";
  for (const level of levels) {
    if ((rank[level] ?? 0) > rank[worst]) worst = level;
  }
  return worst;
}

/**
 * One half of a paired BP string → its band, or 'unclassified' when the half is
 * not a real reading.
 *
 * Number("") and Number(" ") are 0, NOT NaN, so a blank half ("120/", "/80")
 * would otherwise pass Number.isFinite and be classified. Blood pressure has
 * only UPPER bounds, so that 0 lands in-band and an incomplete pair renders
 * clinically green — the exact fabrication this function's caller forbids. A
 * non-positive reading is absent under the react-app#374 zero-absence contract
 * rather than a measurement of zero.
 * @returns {'normal'|'caution'|'critical'|'unclassified'}
 */
function classifyBloodPressureHalf(rawHalf, metricType, customThresholds) {
  if (typeof rawHalf !== "string" || rawHalf.trim() === "") return "unclassified";
  const num = Number(rawHalf);
  if (!Number.isFinite(num) || num <= 0) return "unclassified";
  return classifyVitalSeverity(num, metricType, customThresholds);
}

/**
 * Paired BP string "120/80" or "120/—" → worst of systolic/diastolic.
 * A missing half contributes 'unclassified' (never a fabricated normal), and
 * a pair with an unreadable half is never labeled normal on the strength of
 * the other half alone — a known warning/critical half still surfaces, but
 * "120/—" is unclassified, not green.
 * @returns {'normal'|'caution'|'critical'|'unclassified'}
 */
export function classifyBloodPressureSeverity(pairedValue, customThresholds = {}) {
  if (pairedValue === null || pairedValue === undefined) return "unclassified";
  const raw = String(pairedValue);
  const [sysRaw, diaRaw] = raw.split("/");
  const sysLevel = classifyBloodPressureHalf(
    sysRaw,
    "blood_pressure_systolic",
    customThresholds,
  );
  const diaLevel = classifyBloodPressureHalf(
    diaRaw,
    "blood_pressure_diastolic",
    customThresholds,
  );
  const worst = worstVitalSeverity(sysLevel, diaLevel);
  if (
    worst === "normal" &&
    (sysLevel === "unclassified" || diaLevel === "unclassified")
  ) {
    return "unclassified";
  }
  return worst;
}

/**
 * Baseline-deviation severity for metrics without absolute bands (e.g. temp).
 * |pct| < 10 → normal (green); < 20 → caution (yellow); else critical (red).
 * @returns {'normal'|'caution'|'critical'}
 */
export function classifyBaselineSeverity(deviationPct) {
  if (typeof deviationPct !== "number" || !Number.isFinite(deviationPct)) {
    return "normal";
  }
  const mag = Math.abs(deviationPct);
  if (mag >= 20) return "critical";
  if (mag >= 10) return "caution";
  return "normal";
}

/**
 * Classify a reading: configured clinical bands govern whenever the metric has
 * any; baseline deviation applies ONLY to metrics without bands (e.g. temp).
 *
 * Bands are the green=in-band contract shared with the backend
 * vitalsThresholdEngine and the caregiver-admin threshold UI — a reading
 * inside its band must never be escalated by baseline deviation (HR 72 with a
 * baseline of 55 is in-band, not critical). Baseline deviation stays visible
 * separately via the ±N% delta badge / outOfRange flag.
 *
 * @returns {'normal'|'caution'|'critical'|'unclassified'}
 */
export function classifyVitalReading({
  value,
  metricType,
  customThresholds,
  deviationPct,
} = {}) {
  const absolute = classifyVitalSeverity(value, metricType, customThresholds);
  if (absolute !== "unclassified") return absolute;
  // value <= 0 matters here too: classifyVitalSeverity rejects a non-positive
  // reading, but for an UNBANDED metric that rejection is indistinguishable
  // from "no bands", so without this the baseline rule below would reclassify
  // an ABSENT reading as normal — green on missing data.
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "unclassified";
  }
  if (typeof deviationPct !== "number" || !Number.isFinite(deviationPct)) {
    return "unclassified";
  }
  return classifyBaselineSeverity(deviationPct);
}

/** Light-surface text that still reads green / yellow / red and meets AA on white. */
const LIGHT_RANGE_TEXT = Object.freeze({
  normal: "#1B7A4E",
  caution: "#9A6B00",
  critical: "#C62828",
});

/**
 * Color for a severity level, or null for 'unclassified' / unknown levels so
 * callers fall back to a neutral text color — an unclassifiable reading must
 * not borrow the green of severityToken's normal fallback.
 * @param {'normal'|'caution'|'critical'|'unclassified'|string} level
 * @param {{ surface?: 'dark'|'light' }} [opts] dark = designTokens (web); light = AA text
 */
export function vitalRangeColor(level, { surface = "dark" } = {}) {
  const key = level === "warning" ? "caution" : level;
  if (key !== "normal" && key !== "caution" && key !== "critical") return null;
  if (surface === "light") {
    return LIGHT_RANGE_TEXT[key] || LIGHT_RANGE_TEXT.normal;
  }
  if (key === "caution") return severity.caution.color;
  return severityToken(key).color;
}

/** @returns {string|null} display label, or null for 'unclassified'/unknown */
export function vitalRangeLabel(level) {
  const key = level === "warning" ? "caution" : level;
  if (key === "caution") return "Warning";
  if (key !== "normal" && key !== "critical") return null;
  return severityToken(key).label;
}

/**
 * Visible NON-COLOR cue for abnormal bands ('' for normal/unclassified) —
 * severity must never be conveyed by color alone (render aria-hidden; pair
 * with vitalSeverityA11yLabel in the accessible name).
 */
export function vitalSeverityMarker(level) {
  const key = level === "warning" ? "caution" : level;
  if (key === "critical") return "‼";
  if (key === "caution") return "!";
  return "";
}

/**
 * Severity phrase for accessible names, or null when there is nothing to
 * announce ('unclassified'/unknown).
 */
export function vitalSeverityA11yLabel(level) {
  const key = level === "warning" ? "caution" : level;
  if (key === "critical") return "Critical";
  if (key === "caution") return "Warning";
  if (key === "normal") return "Normal range";
  return null;
}

/**
 * Visually-hidden style (clip-rect, same pattern as AlertsRail's live
 * regions) for announcing severity words without altering the visual cell.
 */
export const srOnlyStyle = Object.freeze({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
});
